import { useCallback, useEffect, useRef, useState } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import { getDefaultData } from '../../lib/journal-utils.js';
import type { StudioCreateRequest } from './useStudioCommands.js';

export interface MaterialRef {
  readonly kind: 'journal' | 'sheet';
  readonly id: string;
}

type JournalType = 'npc' | 'location' | 'quest' | 'faction' | 'handout' | 'lore';

/**
 * One material operation of a beat that is not confirmed yet. Nothing here is
 * shown as saved: the list of materials is the server's (serverState.beats).
 */
export type MaterialOp =
  | {
      readonly key: string;
      readonly kind: 'add' | 'remove';
      readonly beatId: string;
      readonly ref: MaterialRef;
      /** pending — sent; failed — refused, «Retry» re-sends it; offline — answer lost, re-sent after the rejoin. */
      readonly status: 'pending' | 'failed' | 'offline';
      readonly error?: string;
      /** A refusal that a retry would get again (e.g. the entry became the prep source): no «Retry». */
      readonly final?: boolean;
    }
  | {
      readonly key: string;
      readonly kind: 'create';
      readonly beatId: string;
      readonly name: string;
      readonly entryType: JournalType;
      /**
       * pending — creating; failed — certainly not created, «Retry» creates;
       * unknown — answer lost, asked after the rejoin; unresolved — the server
       * could not say: the entry may exist, the GM checks the library.
       */
      readonly status: 'pending' | 'failed' | 'unknown' | 'unresolved';
      readonly error?: string;
    };

interface Deps {
  readonly create: (request: StudioCreateRequest) => void;
  readonly campaignId: string;
  readonly t: (key: keyof TranslationKeys) => string;
  /** The beat's confirmed materials as of the latest render; undefined once the beat is gone. */
  readonly materialsOf: (beatId: string) => readonly MaterialRef[] | undefined;
  /** The beat's prep source (journalEntryId) as of the latest render. */
  readonly sourceOf: (beatId: string) => string | null | undefined;
}

const refKey = (beatId: string, ref: MaterialRef): string => `${beatId} ${ref.kind} ${ref.id}`;

/**
 * A beat's explicit materials (GM Studio 3.1): add / remove one typed ref, or
 * create a journal entry and add it. Each operation is its own server command,
 * correlated by requestId (useStudioCommands). add/remove are idempotent on the
 * server, so a retry or a re-send after a lost answer never duplicates. A
 * created entry is added to the beat the GM started from — never to the beat
 * selected meanwhile, and not at all once that beat is deleted (the entry
 * stays in the journal). A lost answer to a create is recovered by asking the
 * server (package 3); «unknown» there creates nothing again.
 */
export function useBeatMaterials(deps: Deps): {
  readonly ops: readonly MaterialOp[];
  readonly add: (beatId: string, ref: MaterialRef) => void;
  readonly remove: (beatId: string, ref: MaterialRef) => void;
  readonly createAndAdd: (beatId: string, entryType: JournalType, name: string) => void;
  readonly retry: (key: string) => void;
  readonly dismiss: (key: string) => void;
  /** The beats are gone: nothing of theirs is sent or linked any more. */
  readonly cancel: (beatIds: readonly string[]) => void;
} {
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const opsRef = useRef<readonly MaterialOp[]>([]);
  const [ops, setOps] = useState<readonly MaterialOp[]>([]);
  // Beats whose operations were cancelled (deleted): a late answer does nothing.
  const cancelledRef = useRef(new Set<string>());
  const nextRef = useRef(0);

  const put = useCallback((op: MaterialOp): void => {
    const next = [...opsRef.current.filter((o) => o.key !== op.key), op];
    opsRef.current = next;
    setOps(next);
  }, []);
  const drop = useCallback((key: string): void => {
    const next = opsRef.current.filter((o) => o.key !== key);
    if (next.length === opsRef.current.length) return;
    opsRef.current = next;
    setOps(next);
  }, []);
  const find = (key: string): MaterialOp | undefined => opsRef.current.find((o) => o.key === key);

  const sendRef = useCallback(
    (kind: 'add' | 'remove', beatId: string, ref: MaterialRef): void => {
      const key = `${kind} ${refKey(beatId, ref)}`;
      if (cancelledRef.current.has(beatId)) return;
      const other = `${kind === 'add' ? 'remove' : 'add'} ${refKey(beatId, ref)}`;
      drop(other);
      put({ key, kind, beatId, ref, status: 'pending' });
      const { t } = depsRef.current;
      depsRef.current.create({
        command: kind === 'add' ? 'beat:add-material' : 'beat:remove-material',
        echo: kind === 'add' ? 'beat:material-added' : 'beat:material-removed',
        name: '',
        failTitle: t(kind === 'add' ? 'studio.materials.addFailed' : 'studio.materials.removeFailed'),
        payload: { campaignId: depsRef.current.campaignId, beatId, ref: { kind: ref.kind, id: ref.id } },
        matches: (echo, requestId) => (echo.requestId === requestId ? ref.id : undefined),
        onCreated: (): void => drop(key),
        onFailed: (reason: string, final?: boolean): void => {
          if (find(key) !== undefined) put({ key, kind, beatId, ref, status: 'failed', error: reason, final });
        },
        // The entry is (became) the beat's prep source — its own role, not a material.
        finalCodes: ['material_is_prep_source'],
        onUnknown: (): void => {
          if (find(key) !== undefined) put({ key, kind, beatId, ref, status: 'offline' });
        },
        // Idempotent on the server: re-sent with the same requestId after the rejoin.
        recover: 'resend',
        alive: (): boolean => !cancelledRef.current.has(beatId) && find(key) !== undefined,
        retry: (): void => {
          const op = find(key);
          if (op !== undefined && op.status === 'failed') sendRef(kind, beatId, ref);
        },
      });
    },
    [put, drop],
  );

  const add = useCallback(
    (beatId: string, ref: MaterialRef): void => {
      const pending = find(`add ${refKey(beatId, ref)}`);
      if (pending !== undefined && pending.status !== 'failed') return; // a double click sends once
      if (depsRef.current.materialsOf(beatId)?.some((m) => m.kind === ref.kind && m.id === ref.id) === true) return;
      if (ref.kind === 'journal' && ref.id === depsRef.current.sourceOf(beatId)) return; // one record, one role
      sendRef('add', beatId, ref);
    },
    [sendRef],
  );

  const remove = useCallback(
    (beatId: string, ref: MaterialRef): void => {
      const pending = find(`remove ${refKey(beatId, ref)}`);
      if (pending !== undefined && pending.status !== 'failed') return;
      sendRef('remove', beatId, ref);
    },
    [sendRef],
  );

  const startCreate = useCallback(
    (key: string, beatId: string, entryType: JournalType, name: string): void => {
      if (cancelledRef.current.has(beatId)) return;
      put({ key, kind: 'create', beatId, name, entryType, status: 'pending' });
      depsRef.current.create({
        command: 'journal:create',
        echo: 'journal:created',
        name,
        // visibility omitted → the server default 'gm'.
        payload: { entryType, name, data: getDefaultData(entryType) },
        matches: (echo, requestId) =>
          echo.requestId === requestId ? (echo.entry as { id?: string } | undefined)?.id : undefined,
        onCreated: (entryId: string): void => {
          drop(key);
          // Added to the beat the GM started from — if it still exists.
          if (cancelledRef.current.has(beatId) || depsRef.current.materialsOf(beatId) === undefined) return;
          sendRef('add', beatId, { kind: 'journal', id: entryId });
        },
        onFailed: (reason: string): void => {
          if (find(key) !== undefined) put({ key, kind: 'create', beatId, name, entryType, status: 'failed', error: reason });
        },
        onUnknown: (): void => {
          if (find(key) !== undefined) put({ key, kind: 'create', beatId, name, entryType, status: 'unknown' });
        },
        onUnresolved: (reason: string): void => {
          if (find(key) !== undefined) put({ key, kind: 'create', beatId, name, entryType, status: 'unresolved', error: reason });
        },
        // After a lost answer the server is asked what this request created — never a second create.
        recover: 'ask',
        alive: (): boolean => !cancelledRef.current.has(beatId) && find(key) !== undefined,
        retry: (): void => {
          const op = find(key);
          if (op !== undefined && op.status === 'failed') startCreate(key, beatId, entryType, name);
        },
      });
    },
    [put, drop, sendRef],
  );

  const createAndAdd = useCallback(
    (beatId: string, entryType: JournalType, name: string): void => {
      startCreate(`create ${++nextRef.current}`, beatId, entryType, name);
    },
    [startCreate],
  );

  // Inline «Retry»: only a certain refusal is retried (the message's Retry does the same, once).
  const retry = useCallback(
    (key: string): void => {
      const op = find(key);
      if (op === undefined || op.status !== 'failed') return;
      if (op.kind !== 'create' && op.final === true) return;
      if (op.kind === 'create') startCreate(key, op.beatId, op.entryType, op.name);
      else sendRef(op.kind, op.beatId, op.ref);
    },
    [startCreate, sendRef],
  );

  const cancel = useCallback(
    (beatIds: readonly string[]): void => {
      for (const beatId of beatIds) cancelledRef.current.add(beatId);
      const next = opsRef.current.filter((o) => !beatIds.includes(o.beatId));
      if (next.length !== opsRef.current.length) {
        opsRef.current = next;
        setOps(next);
      }
    },
    [],
  );

  // Another campaign: none of these operations.
  useEffect(() => {
    return (): void => {
      opsRef.current = [];
      setOps([]);
      cancelledRef.current.clear();
    };
  }, [deps.campaignId]);

  return { ops, add, remove, createAndAdd, retry, dismiss: drop, cancel };
}
